/* eslint-disable react/no-unknown-property */
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";

// Material Kit 2 React components
import MKBox from "components/MKBox";
import MKTypography from "components/MKTypography";

// Reusable Components
import SubTitleContents from "reusables/TextFormats/SubTitleContents";
import NotableInfo from "reusables/TextFormats/NotableInfo";
import UnNumberedList from "reusables/TextFormats/UnNumberedList";
import Email from "reusables/Contact/Email";

// Images

function AppPrivacy_Section1() {
  const lineSpacing_lg = <MKBox mb={12} />;
  const lineSpacing_sm = <MKBox mb={2} />;

  return (
    <Container>
      <Grid container alignItems="center" width={"100%"}>
        <Grid item xs={12}>
          <SubTitleContents
            title="Introduction"
            contents={
              <>
                This privacy policy explains how Spechs Property Services (&quot;we&quot;,
                &quot;us&quot;, or &quot;our&quot;) collects, uses, stores, and protects your
                personal information when you use the SPECHS mobile application (&quot;the
                App&quot;). The App is a workforce management tool designed for staff scheduling,
                time tracking, and job assignment.
                {lineSpacing_sm}
                By using the App, you agree to the collection and use of information in accordance
                with this policy. We are committed to protecting your privacy and complying with the
                General Data Protection Regulation (GDPR) and all applicable data protection laws.
                {lineSpacing_sm}
                If you have any questions, please contact us at{" "}
                <MKTypography component="span" color="info" fontWeight="bold">
                  <Email
                    email="admin@spechs.ie"
                    subject="App Privacy Inquiry"
                    body="Hello, I have a question about the SPECHS app privacy policy."
                  >
                    admin@spechs.ie
                  </Email>
                </MKTypography>
                .
              </>
            }
          />
          {lineSpacing_lg}
          <SubTitleContents
            title="Information we collect"
            contents={
              <>
                When you use the App, we may collect and process the following categories of
                personal data:
                {lineSpacing_sm}
                <MKTypography variant="h5" mb={1}>
                  Account &amp; profile information
                </MKTypography>
                <UnNumberedList
                  items={[
                    "Name (first and last)",
                    "Email address",
                    "Phone number",
                    "Profile picture (if provided)",
                    "User role (e.g. staff, manager)",
                  ]}
                />
                {lineSpacing_sm}
                <MKTypography variant="h5" mb={1}>
                  Location data
                </MKTypography>
                <UnNumberedList
                  items={[
                    "GPS coordinates (latitude and longitude) when you clock in or clock out",
                    "Distance from assigned job site",
                    "Location accuracy data",
                  ]}
                />
                {lineSpacing_sm}
                <MKTypography variant="h5" mb={1}>
                  Work-related data
                </MKTypography>
                <UnNumberedList
                  items={[
                    "Time entries (clock in/out timestamps)",
                    "Job assignments and schedules",
                    "Hours worked and break times",
                    "Manager adjustments to time records",
                  ]}
                />
                {lineSpacing_sm}
                <MKTypography variant="h5" mb={1}>
                  Communication data
                </MKTypography>
                <UnNumberedList
                  items={[
                    "Messages sent and received within the App",
                    "Alerts and notification history",
                  ]}
                />
              </>
            }
          />
          {lineSpacing_lg}
          <SubTitleContents
            title="How we use your information"
            contents={
              <>
                We use the collected information for the following purposes:
                {lineSpacing_sm}
                <UnNumberedList
                  items={[
                    "To provide and operate the workforce management service",
                    "To verify your identity and manage your account",
                    "To track work hours and attendance for scheduling and payroll purposes",
                    "To verify your location at clock in/out for job site compliance",
                    "To facilitate communication between staff and management",
                    "To send alerts and notifications relevant to your work",
                    "To improve the App and our services",
                  ]}
                />
              </>
            }
          />
          {lineSpacing_lg}
          <SubTitleContents
            title="Lawful basis for processing"
            contents={
              <>
                We process your personal data on the following legal bases under the GDPR:
                {lineSpacing_sm}
                <UnNumberedList
                  items={[
                    <>
                      <MKTypography component="span" fontWeight="bold">
                        Contractual necessity
                      </MKTypography>{" "}
                      &mdash; to fulfil our obligations under your employment or service agreement,
                      including scheduling, time tracking, and payroll.
                    </>,
                    <>
                      <MKTypography component="span" fontWeight="bold">
                        Legitimate interest
                      </MKTypography>{" "}
                      &mdash; to manage workforce operations, ensure job site compliance, and
                      improve our services.
                    </>,
                    <>
                      <MKTypography component="span" fontWeight="bold">
                        Consent
                      </MKTypography>{" "}
                      &mdash; for location data collection, which you can manage through your device
                      settings.
                    </>,
                  ]}
                />
              </>
            }
          />
          {lineSpacing_lg}
          <SubTitleContents
            title="Third-party services"
            contents={
              <>
                The App uses the following third-party services to operate:
                {lineSpacing_sm}
                <MKTypography variant="h5" mb={1}>
                  Firebase (Google)
                </MKTypography>
                <MKTypography variant="body1" color="text" mb={1} sx={{ textAlign: "justify" }}>
                  We use Firebase for authentication, data storage, and push notifications. Your
                  data is stored on Firebase servers and is subject to Google&apos;s privacy policy
                  and data processing terms.
                </MKTypography>
                {lineSpacing_sm}
                <MKTypography variant="h5" mb={1}>
                  Map &amp; location services
                </MKTypography>
                <MKTypography variant="body1" color="text" sx={{ textAlign: "justify" }}>
                  We use mapping services to display job site locations and verify your position
                  during clock in/out. Location data is only collected when you actively use these
                  features and have granted permission.
                </MKTypography>
              </>
            }
          />
          {lineSpacing_lg}
          <SubTitleContents
            title="Device permissions"
            contents={
              <>
                The App may request the following permissions on your device:
                {lineSpacing_sm}
                <UnNumberedList
                  items={[
                    <>
                      <MKTypography component="span" fontWeight="bold">
                        Location
                      </MKTypography>{" "}
                      &mdash; to record your position when clocking in/out and to display your
                      location on the map. You can revoke this permission at any time through your
                      device settings.
                    </>,
                    <>
                      <MKTypography component="span" fontWeight="bold">
                        Notifications
                      </MKTypography>{" "}
                      &mdash; to send you alerts about schedule changes, messages, and other
                      work-related updates.
                    </>,
                    <>
                      <MKTypography component="span" fontWeight="bold">
                        Internet access
                      </MKTypography>{" "}
                      &mdash; required for the App to communicate with our servers.
                    </>,
                  ]}
                />
              </>
            }
          />
          {lineSpacing_lg}
          <SubTitleContents
            title="Data storage and security"
            contents={
              <>
                Your data is stored securely using Firebase cloud services with encryption in
                transit (HTTPS/TLS) and at rest. We also store limited data locally on your device
                (such as authentication tokens) to keep you signed in.
                {lineSpacing_sm}
                We implement appropriate technical and organisational measures to protect your
                personal data against unauthorised access, alteration, disclosure, or destruction.
                Access to your data is restricted through role-based access controls.
              </>
            }
          />
          {lineSpacing_lg}
          <SubTitleContents
            title="Data retention"
            contents={
              <>
                We retain your personal data for as long as necessary to fulfil the purposes for
                which it was collected, including:
                {lineSpacing_sm}
                <UnNumberedList
                  items={[
                    "Account data is retained while you are an active user of the App.",
                    "Time entries and work records are retained as required for payroll and legal compliance.",
                    "Communication data (messages) is retained for the duration of your employment.",
                    "Upon account deletion or termination, your personal data will be securely removed or anonymised within a reasonable timeframe, except where retention is required by law.",
                  ]}
                />
              </>
            }
          />
          {lineSpacing_lg}
          <SubTitleContents
            title="Your rights"
            contents={
              <>
                Under the GDPR, you have the following rights regarding your personal data:
                {lineSpacing_sm}
                <UnNumberedList
                  items={[
                    <>
                      <MKTypography component="span" fontWeight="bold">
                        Right of access
                      </MKTypography>{" "}
                      &mdash; you can request a copy of the personal data we hold about you.
                    </>,
                    <>
                      <MKTypography component="span" fontWeight="bold">
                        Right to rectification
                      </MKTypography>{" "}
                      &mdash; you can request correction of inaccurate or incomplete data.
                    </>,
                    <>
                      <MKTypography component="span" fontWeight="bold">
                        Right to erasure
                      </MKTypography>{" "}
                      &mdash; you can request deletion of your personal data.
                    </>,
                    <>
                      <MKTypography component="span" fontWeight="bold">
                        Right to restrict processing
                      </MKTypography>{" "}
                      &mdash; you can request that we limit how we use your data.
                    </>,
                    <>
                      <MKTypography component="span" fontWeight="bold">
                        Right to data portability
                      </MKTypography>{" "}
                      &mdash; you can request your data in a structured, machine-readable format.
                    </>,
                    <>
                      <MKTypography component="span" fontWeight="bold">
                        Right to object
                      </MKTypography>{" "}
                      &mdash; you can object to processing based on legitimate interest.
                    </>,
                  ]}
                />
                {lineSpacing_sm}
                To exercise any of these rights, please contact our Data Protection Officer at{" "}
                <MKTypography component="span" color="info" fontWeight="bold">
                  <Email
                    email="admin@spechs.ie"
                    subject="Data Rights Request - SPECHS App"
                    body="Hello, I would like to exercise my data protection rights regarding the SPECHS mobile app."
                  >
                    admin@spechs.ie
                  </Email>
                </MKTypography>
                .{lineSpacing_sm}
                You also have the right to lodge a complaint with the Data Protection Commission
                (DPC) of Ireland. However, we would appreciate the opportunity to address your
                concerns before you approach the DPC.
              </>
            }
          />
          {lineSpacing_lg}
          <SubTitleContents
            title="Children's privacy"
            contents={
              <>
                The App is intended for use by employees aged 18 and over. We do not knowingly
                collect personal information from children under 18. If you believe we have
                inadvertently collected such information, please contact us and we will promptly
                delete it.
              </>
            }
          />
          {lineSpacing_lg}
          <SubTitleContents
            title="Changes to this policy"
            contents={
              <>
                We may update this privacy policy from time to time to reflect changes in our
                practices or for legal, regulatory, or operational reasons. We will notify you of
                any significant changes through the App or by other appropriate means. Your
                continued use of the App after changes are posted constitutes your acceptance of the
                updated policy.
              </>
            }
          />
          {lineSpacing_lg}
          <SubTitleContents
            title="Contact us"
            contents={
              <>
                If you have any questions or concerns about this privacy policy or our data
                practices, please contact us:
                <MKBox mb={2} />
                <UnNumberedList
                  items={[
                    <>
                      <MKTypography component="span" color="text">
                        Office:{" "}
                      </MKTypography>
                      <MKTypography component="span">+353 21 206 3133</MKTypography>
                    </>,
                    <>
                      <MKTypography component="span" color="text">
                        Email:{" "}
                      </MKTypography>
                      <MKTypography component="span" fontWeight="bold">
                        <Email
                          email="admin@spechs.ie"
                          subject="App Privacy Inquiry"
                          body="Hello, I have a question about the SPECHS app privacy policy."
                        >
                          admin@spechs.ie
                        </Email>{" "}
                      </MKTypography>
                    </>,
                  ]}
                />
              </>
            }
          />
          <NotableInfo
            text={
              "Effective date: February 2026. This policy is reviewed annually or as necessary."
            }
          />
        </Grid>
      </Grid>
    </Container>
  );
}

export default AppPrivacy_Section1;
